import { useEffect, useRef } from 'react';
import { putProgress } from '../api/progress';

interface Opts {
  mediaId: string;
  videoEl: HTMLVideoElement | null;
  durationSec?: number | null;
  intervalMs?: number;
}

/**
 * Throttled watch-progress sync.
 *
 *  - Every `intervalMs` while playing, PUT the current position.
 *  - PUT immediately on `pause`, `seeked`, and `ended` so a tab close after
 *    a pause doesn't lose state.
 *  - PUT once on unmount with the final position (best-effort).
 *
 *  We don't mark `completed=true` until the video reports `ended` or the
 *  position is within 30 s of the duration — that filters credit-skipping
 *  and accidental seeks-to-end.
 */
export function useProgressSync({ mediaId, videoEl, durationSec, intervalMs = 10000 }: Opts) {
  const lastSentRef = useRef<number>(-1);
  const sendingRef = useRef<boolean>(false);

  useEffect(() => {
    if (!videoEl) return;

    let timer: ReturnType<typeof setInterval> | null = null;

    const send = async (force = false) => {
      const v = videoEl;
      if (!v) return;
      const pos = Math.floor(v.currentTime || 0);
      const dur = Math.floor(v.duration || durationSec || 0);
      if (!force && Math.abs(pos - lastSentRef.current) < 5) return;
      if (sendingRef.current) return;
      sendingRef.current = true;
      try {
        const completed = !!(dur && pos >= dur - 30);
        await putProgress(mediaId, {
          positionSec: pos,
          durationSec: dur || null,
          completed,
          kind: 'movie',
        });
        lastSentRef.current = pos;
      } catch {
        // Swallow — periodic retries cover transient failures.
      } finally {
        sendingRef.current = false;
      }
    };

    const onPlay = () => {
      if (timer) clearInterval(timer);
      timer = setInterval(() => { send(false); }, intervalMs);
    };
    const onPause = () => {
      if (timer) clearInterval(timer);
      timer = null;
      send(true);
    };
    const onEnded = () => {
      if (timer) clearInterval(timer);
      timer = null;
      // Force a "completed" tick. The server flips `completed` when the
      // position is within 30 s of duration; on `ended` we definitely are.
      send(true);
    };
    const onSeeked = () => send(false);

    videoEl.addEventListener('play', onPlay);
    videoEl.addEventListener('pause', onPause);
    videoEl.addEventListener('ended', onEnded);
    videoEl.addEventListener('seeked', onSeeked);

    if (!videoEl.paused) onPlay();

    return () => {
      if (timer) clearInterval(timer);
      videoEl.removeEventListener('play', onPlay);
      videoEl.removeEventListener('pause', onPause);
      videoEl.removeEventListener('ended', onEnded);
      videoEl.removeEventListener('seeked', onSeeked);
      // Final flush; we can't await in cleanup, but the request kicks off.
      send(true);
    };
  }, [videoEl, mediaId, durationSec, intervalMs]);
}
